import type { CatalogTaxCategory, TaxCategory, TaxExemptionReason, TaxReviewDueReason } from '@aralere/shared-types';

// ──────────────────────────────────────────────────────────────────────────────
// The SPA MIRROR of api/src/lib/tax-categories.ts (04 #160 WS-A; 02i T3.1). A portal-side
// duplicate on the charge-class.ts precedent: the API lib is not a package and the portal takes no
// edge onto shared-utils (the s115 rule). Keep the two tables IDENTICAL - scripts/tax-resolver-check.mjs
// --preview imports this file and asserts every label and every suggestion case against the server's.
// Admin-local (beside useListControls.ts) because only admin pages consume it: the Services editor,
// the WS-B review page, the WS-E Settings rule editor and the WS-D client tax panel.
// ──────────────────────────────────────────────────────────────────────────────

/** The five a catalog SERVICE may carry, in display order (the select's options). */
export const CATALOG_TAX_CATEGORIES: readonly CatalogTaxCategory[] = [
  'service.professional',
  'software.prewritten',
  'software.custom',
  'digital.product',
  'goods.tangible',
];

/** Plain-language labels (the 12 guide's wording) - the server's TAX_CATEGORY_LABELS verbatim. */
export const TAX_CATEGORY_LABELS: Readonly<Record<TaxCategory, string>> = {
  'service.professional': 'Professional service',
  'software.prewritten': 'Prewritten software / SaaS',
  'software.custom': 'Custom software',
  'digital.product': 'Digital product',
  'goods.tangible': 'Tangible goods',
  shipping: 'Shipping',
  'late-fee': 'Late fee / finance charge',
  'fee.mandatory': 'Mandatory charge (charge class)',
  bundle: 'Package (members decide)',
};

/** [value, label] pairs for the editor's Select (the Services.tsx Select shape). */
export const TAX_CATEGORY_OPTIONS: ReadonlyArray<readonly [CatalogTaxCategory, string]> =
  CATALOG_TAX_CATEGORIES.map((c) => [c, TAX_CATEGORY_LABELS[c]] as const);

/** 04 #160 WS-E: the categories a RULE ROW may name - the server's tax.ts#ROW_CATEGORIES verbatim (every
 *  category except 'fee.mandatory', taxed by its class's setting, and 'bundle', taxed by its members).
 *  The Settings brand-rule editor's category select; checked against the server by the harness --preview. */
export const RULE_TAX_CATEGORIES: readonly TaxCategory[] = [
  'service.professional',
  'software.prewritten',
  'software.custom',
  'digital.product',
  'goods.tangible',
  'shipping',
  'late-fee',
];

export const RULE_TAX_CATEGORY_OPTIONS: ReadonlyArray<readonly [TaxCategory, string]> =
  RULE_TAX_CATEGORIES.map((c) => [c, TAX_CATEGORY_LABELS[c]] as const);

export const taxCategoryLabel = (c: TaxCategory | null | undefined): string =>
  c ? TAX_CATEGORY_LABELS[c] ?? c : 'Not set';

export const isCatalogTaxCategory = (v: unknown): v is CatalogTaxCategory =>
  typeof v === 'string' && (CATALOG_TAX_CATEGORIES as readonly string[]).includes(v);

/** The server's SUGGESTION_TABLE verbatim (order matters: first match wins). */
const SUGGESTION_TABLE: ReadonlyArray<readonly [RegExp, CatalogTaxCategory, string]> = [
  [/\b(custom|bespoke)\b.*\b(software|development|app|application|integration)\b|\b(software|app|application)\b.*\b(custom|bespoke)\b/i, 'software.custom', 'custom software'],
  [/\b(labor|labour|support|helpdesk|help desk|onsite|on-site|retainer|consult(ing|ation)?|managed services?|monitoring|maintenance|installation|setup|set-up|training|migration|project|hourly|troubleshoot(ing)?|repair|service desk)\b/i, 'service.professional', 'a services word'],
  [/\b(e-?book|ebook|download|digital (product|download|asset)|template pack|stock (photo|image))\b/i, 'digital.product', 'a digital-product word'],
  [/\b(m365|microsoft 365|office 365|o365|exchange online|sharepoint|onedrive|intune|entra id|azure ad|copilot)\b/i, 'software.prewritten', 'a Microsoft 365 product'],
  [/\b(1password|bitwarden|lastpass|defender|threatdown|malwarebytes|umbrella|crowdstrike|sentinelone|huntress|datto|veeam|acronis|dropbox|google workspace|zoom|slack|adobe|quickbooks|xero)\b/i, 'software.prewritten', 'a software product name'],
  [/\b(saas|subscription|licen[cs]e|licen[cs]es|seat|seats|per[- ]user|per[- ]seat|antivirus|anti-?malware|edr|mdr|backup software|password manager|cloud (storage|app|software)|software)\b/i, 'software.prewritten', 'a software / subscription word'],
  [/\b(hardware|laptop|desktop|workstation|server|switch|router|appliance|printer|monitor|cable|device|equipment|parts?|peripheral)\b/i, 'goods.tangible', 'a hardware word'],
];

/** A NAME-BASED suggestion - NEVER applied silently: the editor shows it and the admin clicks. */
export function suggestTaxCategory(name: string): { category: CatalogTaxCategory; why: string } | null {
  const n = (name ?? '').trim();
  if (!n) return null;
  for (const [re, category, why] of SUGGESTION_TABLE) {
    if (re.test(n)) return { category, why };
  }
  return null;
}

/** 04 #160 WS-B: the dashboard banner's and the review page's wording for the server's trigger reasons
 *  (api/src/lib/tax-review.ts#reviewDue). SPA-only vocabulary (the server returns the codes). */
export const TAX_REVIEW_REASON_LABELS: Readonly<Record<TaxReviewDueReason, string>> = {
  december: 'The annual December review is open',
  'profile-upcoming': 'A tax rule changes within the next year',
  'profile-stale': 'A state profile has not been reviewed for over a year',
};

/** 04 #160 WS-D: the exemption reasons a client may carry - the server's tax-config.ts#EXEMPTION_REASONS
 *  verbatim (checked by the harness --preview, E7), in the client editor's select order. */
export const TAX_EXEMPTION_REASONS: readonly TaxExemptionReason[] = ['resale', 'exempt-org', 'direct-pay', 'out-of-state-use', 'other'];

/** Plain-language labels for the reasons (the client editor's select and the review page's exemption list). */
export const TAX_EXEMPTION_REASON_LABELS: Readonly<Record<TaxExemptionReason, string>> = {
  resale: 'Resale (the client resells what we sell them)',
  'exempt-org': 'Exempt organization (nonprofit, government, school)',
  'direct-pay': 'Direct pay (the client remits its own tax)',
  'out-of-state-use': 'Out-of-state use',
  other: 'Other',
};

export const TAX_EXEMPTION_REASON_OPTIONS: ReadonlyArray<readonly [TaxExemptionReason, string]> =
  TAX_EXEMPTION_REASONS.map((r) => [r, TAX_EXEMPTION_REASON_LABELS[r]] as const);
